import { apiClient } from './client.js';
import { authApi } from '../../features/auth/api/authApi.js';
import { booksApi } from '../../features/books/api/booksApi.js';
import { editionsApi } from '../../features/editions/api/editionsApi.js';
import { uibnApi } from '../../features/uibn/api/uibnApi.js';
import { verificationApi } from '../../features/verification/api/verificationApi.js';
import { scannerApi } from '../../features/scanner/api/scannerApi.js';
import { dashboardApi } from '../../features/dashboard/api/dashboardApi.js';
import { badgeApi } from '../../features/badges/api/badgeApi.js';
import { notificationsApi } from '../../features/notifications/api/notificationsApi.js';
import { blogApi } from '../../features/blog/api/blogApi.js';
import type { Book, ApiDocumentation, CreateEditionInput } from '../types/index.js';

export const api = {
  // Books & Catalog
  getBooks: booksApi.getBooks,
  searchBooks: booksApi.searchBooks,
  getSearchSuggestions: booksApi.getSearchSuggestions,
  getBookById: booksApi.getBookById,
  createBook: booksApi.createBook,
  bulkCreateBooks: booksApi.bulkCreateBooks,
  updateBook: booksApi.updateBook,
  deleteBook: booksApi.deleteBook,
  downloadCsvDirect: booksApi.downloadCsvDirect,
  getShowcase: booksApi.getShowcase,
  getShowcaseDirectories: booksApi.getShowcaseDirectories,

  // Verification & Status
  updateBookStatus: verificationApi.updateStatus,

  // UIBN & Certificate
  generateUibnPreview: uibnApi.generateUibnPreview,
  previewUibn: uibnApi.previewUibn,
  validateUibn: uibnApi.validateUibn,
  getCertificate: uibnApi.getCertificate,
  verifyCertificateSignature: uibnApi.verifyCertificateSignature,

  // Editions & Tree
  getEditionTree: editionsApi.getEditionTree,
  createEdition: editionsApi.createEdition,
  linkEdition: editionsApi.linkEdition,

  // Scans & Telemetry
  recordScan: scannerApi.recordScan,
  getScanHistory: scannerApi.getScanHistory,

  // Dashboard & Stats
  getDashboardStats: dashboardApi.getDashboardStats,
  getPublicStats: dashboardApi.getPublicStats,

  // Notifications
  getNotifications: notificationsApi.getNotifications,
  markNotificationRead: notificationsApi.markAsRead,
  markAllNotificationsRead: notificationsApi.markAllRead,
  triggerTestNotification: notificationsApi.triggerTestPush,
  deleteNotification: notificationsApi.deleteNotification,
  deleteReadNotifications: notificationsApi.deleteReadNotifications,

  // Badges & Embeds
  getBadgeData: badgeApi.getBadgeData,
  getBadgeSvgUrl: badgeApi.getBadgeSvgUrl,
  getEmbedUrl: badgeApi.getEmbedUrl,

  // Authentication & Users
  login: authApi.login,
  register: authApi.register,
  getMe: authApi.getMe,
  getDemoAccounts: authApi.getDemoAccounts,
  updateProfile: authApi.updateProfile,
  uploadProfileImage: authApi.uploadProfileImage,
  getAllUsers: authApi.getAllUsers,
  getUsers: authApi.getUsers,
  getPublishers: authApi.getPublishers,
  createUser: authApi.createUser,
  updateUserAdmin: authApi.updateUserAdmin,
  updateUserRole: authApi.updateUserRole,
  deleteUser: authApi.deleteUser,
  submitPublisherVerification: authApi.submitPublisherVerification,
  verifyPublisherAdmin: authApi.verifyPublisherAdmin,

  // API Documentation
  async getApiDocumentation(): Promise<ApiDocumentation> {
    return apiClient('/api/docs');
  },
  async getApiDocs(): Promise<ApiDocumentation> {
    return apiClient('/api/docs');
  },

  // Admin Database Backup & Restore
  async listDatabaseBackups(): Promise<{ backups: any[] }> {
    return apiClient('/api/admin/database/backups');
  },
  async createDatabaseBackup(): Promise<{ message: string; backup: any }> {
    return apiClient('/api/admin/database/backup/create', { method: 'POST' });
  },
  async deleteDatabaseBackup(filename: string): Promise<{ message: string }> {
    return apiClient(`/api/admin/database/backup/${encodeURIComponent(filename)}`, { method: 'DELETE' });
  },
  async restoreDatabase(payload: { filename?: string; dumpData?: any }): Promise<{ message: string; restoredCounts: any }> {
    return apiClient('/api/admin/database/restore', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Blog
  getPublishedPosts: blogApi.getPublishedPosts,
  getPostBySlug: blogApi.getPostBySlug,
  getAdminPosts: blogApi.getAdminPosts,
  createPost: blogApi.createPost,
  updatePost: blogApi.updatePost,
  deletePost: blogApi.deletePost,
  likePost: blogApi.likePost,
  askAuthor: blogApi.askAuthor,
  subscribeNewsletter: blogApi.subscribeNewsletter,
  getNewsletterSubscribers: blogApi.getNewsletterSubscribers,
  deleteNewsletterSubscriber: blogApi.deleteNewsletterSubscriber,
  getBlogCategories: blogApi.getCategories,
  getBlogCategoryById: blogApi.getCategoryById,
  createBlogCategory: blogApi.createCategory,
  updateBlogCategory: blogApi.updateCategory,
  deleteBlogCategory: blogApi.deleteCategory,

  // Admin System Settings (R2)
  async getR2Config(): Promise<{ config: any; hasSecretKey?: boolean; isEnvConfigured: boolean }> {
    return apiClient('/api/admin/settings/r2');
  },
  async saveR2Config(config: any): Promise<{ success: true; message: string; hasSecretKey?: boolean; maskedSecretKey?: string }> {
    return apiClient('/api/admin/settings/r2', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },
  async testR2Config(config: any): Promise<{ success: true; message: string }> {
    return apiClient('/api/admin/settings/r2/test', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  },
};

export {
  authApi,
  booksApi,
  editionsApi,
  uibnApi,
  verificationApi,
  scannerApi,
  dashboardApi,
  badgeApi,
  notificationsApi,
  blogApi,
};
